/**
 * LinkedTodoCreator
 *
 * Reusable compact form for creating a new EOS To-Do that is
 * immediately linked to a source entity (rock, headline, meeting,
 * kpi, issue, …) via the polymorphic `linked_items` table.
 *
 * The To-Do keeps its own stable UUID; the link is what ties it to
 * the source. Miss counts, re-commits and history all live on the
 * To-Do side.
 */

import { useState } from 'react';
import { Plus, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSession } from '@/hooks/useSession';
import { useOwnerOptions } from '@/features/rocks/hooks/useRocks';
import { useCreateLinkedItem } from '@/features/rocks/hooks/useLinkedItems';
import { useCreateEosTodo } from '../hooks/useEosTodoMutations';
import { EOS_TODO_PRIORITIES, EOS_TODO_PRIORITY_LABELS } from '../types';
import type { EosTodoPriority } from '../types';

interface Props {
  /** Source entity type (e.g. 'rock', 'headline', 'meeting', 'kpi', 'issue') */
  sourceType: string;
  /** Source entity UUID */
  sourceId: string;
  /** Optional team to pre-fill on the new To-Do */
  teamId?: string | null;
  /** Optional meeting id — sets `created_from_meeting_id` on the To-Do */
  createdFromMeetingId?: string;
  /** Visual size */
  compact?: boolean;
}

function defaultDueDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

export function LinkedTodoCreator({
  sourceType,
  sourceId,
  teamId,
  createdFromMeetingId,
  compact = false,
}: Props) {
  const { user } = useSession();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [ownerId, setOwnerId] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>(defaultDueDate());
  const [priority, setPriority] = useState<EosTodoPriority>('medium');

  const { data: owners = [] } = useOwnerOptions();
  const createTodo = useCreateEosTodo();
  const linkItem = useCreateLinkedItem();

  const reset = () => {
    setTitle('');
    setOwnerId('');
    setDueDate(defaultDueDate());
    setPriority('medium');
  };

  const effectiveOwnerId = ownerId || user?._id || '';
  const canSubmit = !!title.trim() && !!effectiveOwnerId && !createTodo.isPending && !linkItem.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    const todo = await createTodo.mutateAsync({
      title: title.trim(),
      owner_id: effectiveOwnerId,
      team_id: teamId ?? undefined,
      created_from_meeting_id: createdFromMeetingId,
      due_date: dueDate,
      priority,
    });

    await linkItem.mutateAsync({
      source_type: sourceType,
      source_id: sourceId,
      target_type: 'todo',
      target_id: todo.id,
    });

    reset();
    setOpen(false);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors ${compact ? 'py-1 px-2' : 'py-1.5 px-2'}`}
      >
        <Plus className="h-3 w-3" />
        New To-Do
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border rounded-md p-3 space-y-2 bg-muted/30"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">
          New To-Do
        </Label>
        <button
          type="button"
          onClick={() => { reset(); setOpen(false); }}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <Input
        autoFocus
        placeholder="What needs to be done in 7 days?"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="h-8 text-sm"
      />

      <div className="grid grid-cols-2 gap-2">
        <Select value={effectiveOwnerId} onValueChange={setOwnerId}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Owner" />
          </SelectTrigger>
          <SelectContent>
            {owners.map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.name || o.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="h-8 text-xs"
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <Select value={priority} onValueChange={(v) => setPriority(v as EosTodoPriority)}>
          <SelectTrigger className="h-8 text-xs w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EOS_TODO_PRIORITIES.map((p) => (
              <SelectItem key={p} value={p}>
                {EOS_TODO_PRIORITY_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button type="submit" size="sm" disabled={!canSubmit}>
          {(createTodo.isPending || linkItem.isPending) && (
            <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
          )}
          Create & Link
        </Button>
      </div>
    </form>
  );
}
